import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { SkeletonLoader, BaseLayout } from '@/components';
import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@/components/ui';
import { AccountForm } from '../../../../components/forms/account-form';
import {
  getAccount,
  activateAccount,
  deactivateAccount,
} from '../../../../api/account.api';
import type { Account } from '../../../../types/accounts.types';
import { useToast } from '../../../../contexts/toast-context';
import { TOAST_TYPES } from '../../../../constants/common.constants';
import { FORM_MODES } from '../../../../constants/form.constants';
import { AuthError } from '../../../../utils/errors';

export const Route = createFileRoute('/accounts/$uuid/edit/')({
  component: EditAccount,
});

function EditAccount() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const fetchAccount = async () => {
    try {
      setIsLoading(true);
      const data = await getAccount(uuid);
      setAccount(data);
    } catch (error) {
      addToast(`Failed to fetch account`, {
        type: TOAST_TYPES.ERROR,
        title: 'Fetch Error',
      });
      if (!(error instanceof AuthError)) {
        navigate({ to: '/dashboard' });
      }
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchAccount();
  }, [uuid]);

  const handleToggleStatusClick = () => {
    setShowConfirmDialog(true);
  };

  const handleCancelToggle = () => {
    setShowConfirmDialog(false);
  };

  const handleConfirmToggle = async () => {
    if (!account) return;

    try {
      setIsTogglingStatus(true);
      setShowConfirmDialog(false);

      if (account.is_active) {
        await deactivateAccount(uuid);
        addToast('Account deactivated successfully', {
          type: TOAST_TYPES.SUCCESS,
          title: 'Success',
        });
      } else {
        await activateAccount(uuid);
        addToast('Account activated successfully', {
          type: TOAST_TYPES.SUCCESS,
          title: 'Success',
        });
      }
      // Refetch account data to update UI
      await fetchAccount();
    } catch (error) {
      addToast(`${error}`, {
        type: TOAST_TYPES.ERROR,
        title: 'Status Update Error',
      });
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleSuccess = () => {
    navigate({ to: `/accounts/${uuid}` });
  };

  const handleCancel = () => {
    navigate({ to: `/accounts/${uuid}` });
  };

  if (isLoading) {
    return (
      <BaseLayout
        header={{
          headerText: 'Edit Account',
        }}
        breadcrumbs={{
          showBreadcrumbs: true,
          breadcrumbText: ['Accounts', 'Edit Account'],
        }}
      >
        <Card>
          <CardContent>
            <SkeletonLoader count={10} height={30} />
          </CardContent>
        </Card>
      </BaseLayout>
    );
  }

  if (!account) {
    return null;
  }

  return (
    <>
      <BaseLayout
        header={{
          headerText: 'Edit Account',
        }}
        breadcrumbs={{
          showBreadcrumbs: true,
          breadcrumbText: [account.name, 'Edit'],
        }}
      >
        <AccountForm
          mode={FORM_MODES.EDIT}
          initialData={account}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          isActive={account.is_active}
          isTogglingStatus={isTogglingStatus}
          onToggleStatus={handleToggleStatusClick}
        />
      </BaseLayout>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {account.is_active ? 'Deactivate Account' : 'Activate Account'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to{' '}
              {account.is_active ? 'deactivate' : 'activate'} this account?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline-secondary"
              size="medium"
              onClick={handleCancelToggle}
            >
              Cancel
            </Button>
            <Button
              variant={account.is_active ? 'outline-danger' : 'outline-success'}
              size="medium"
              onClick={handleConfirmToggle}
            >
              {account.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
